/**
 * Seeded OPCON assets shown under AOR hierarchy cards while
 * USCG FOC Requirements is on. Data origin is ALMIS; home-unit
 * association is preserved even when the asset supports another AOR.
 */

export type UscgFocAorAssetType = 'Boat' | 'Aircraft' | 'Cutter';

export type UscgFocAorAsset = {
  id: string;
  name: string;
  assetType: UscgFocAorAssetType;
  /** Authoritative home unit; geographic OPCON does not reassign this. */
  homeUnit: string;
  /** Unit that currently has OPCON of the asset. */
  opconUnit: string;
  source: 'ALMIS';
};

function homeUnitFor(aorName: string): string {
  if (/station/i.test(aorName)) return aorName;
  const short = aorName.replace(/^(Sector|District|Area)\s+/i, '').trim();
  return `Station ${short}`;
}

/**
 * Exactly three stable ALMIS assets under OPCON of the given AOR.
 * Ids are deterministic so tour targets and list keys stay stable.
 */
export function getUscgFocAorAssets(
  aorId: string,
  aorName: string
): UscgFocAorAsset[] {
  const homeUnit = homeUnitFor(aorName);
  const short = aorName
    .replace(/^(Boat Station|Station|Sector|District|Area)\s+/i, '')
    .trim();
  const label = short || aorName;

  return [
    {
      id: `${aorId}-almis-boat`,
      name: `RB-M ${label}`,
      assetType: 'Boat',
      homeUnit,
      opconUnit: aorName,
      source: 'ALMIS',
    },
    {
      id: `${aorId}-almis-aircraft`,
      name: `MH-65 ${label}`,
      assetType: 'Aircraft',
      homeUnit: homeUnit.replace(/^Station\b/i, 'Air Station'),
      opconUnit: aorName,
      source: 'ALMIS',
    },
    {
      id: `${aorId}-almis-cutter`,
      name: `WPB ${label}`,
      assetType: 'Cutter',
      homeUnit,
      opconUnit: aorName,
      source: 'ALMIS',
    },
  ];
}
